import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { VideoPlayer } from "@seeen/player";
import { ensureOverlayCss } from "./sheet";
import type { VideoItem, CtaOverlay } from "../shared/types";

export function ReelsPlayer({
  videos,
  initialIndex,
  overlays,
  onClose,
}: {
  videos: VideoItem[];
  initialIndex: number;
  overlays: CtaOverlay[];
  onClose: () => void;
}) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Inject overlay CSS into the document head once (light DOM portal, no shadow root).
  useEffect(() => {
    ensureOverlayCss();
  }, []);

  // Jump to the selected reel without animation.
  // Runs after the portal is committed to the light DOM so itemRefs are set.
  useEffect(() => {
    itemRefs.current[initialIndex]?.scrollIntoView({ behavior: "instant" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock body scroll while the overlay is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Track the active reel via IntersectionObserver on the scroll container.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observers = itemRefs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveIndex(i);
        },
        { root: container, threshold: 0.6 },
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, [videos.length]);

  // Keyboard navigation.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowRight")
        scrollToIndex(Math.min(activeIndex + 1, videos.length - 1));
      if (e.key === "ArrowUp" || e.key === "ArrowLeft")
        scrollToIndex(Math.max(activeIndex - 1, 0));
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, videos.length, onClose]);

  function scrollToIndex(i: number) {
    itemRefs.current[i]?.scrollIntoView({ behavior: "smooth" });
  }

  // Portal directly to document.body (light DOM) — no shadow root so that:
  // 1. itemRefs are available immediately when effects run (no two-render delay)
  // 2. position:fixed is relative to the viewport without any shadow root containment issues
  // The VideoPlayer inside still has its own internal shadow root for CSS isolation.
  return createPortal(
    <div className="seeen-reels-overlay">
      {/* Close */}
      <button
        type="button"
        className="seeen-reels-btn seeen-reels-close"
        onClick={onClose}
        aria-label="Close"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Prev / Next */}
      <button
        type="button"
        className="seeen-reels-btn seeen-reels-nav seeen-reels-prev"
        onClick={() => scrollToIndex(Math.max(activeIndex - 1, 0))}
        aria-label="Previous"
        disabled={activeIndex === 0}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 15l7-7 7 7" />
        </svg>
      </button>
      <button
        type="button"
        className="seeen-reels-btn seeen-reels-nav seeen-reels-next"
        onClick={() =>
          scrollToIndex(Math.min(activeIndex + 1, videos.length - 1))
        }
        aria-label="Next"
        disabled={activeIndex === videos.length - 1}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Counter */}
      <div className="seeen-reels-counter">
        {activeIndex + 1} / {videos.length}
      </div>

      {/* Scrollable snap container */}
      <div ref={containerRef} className="seeen-reels-scroll">
        {videos.map((video, i) => (
          <div
            key={video.id}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            className="seeen-reels-slide"
          >
            {i === activeIndex && (
              <div className="seeen-reels-video-wrap">
                <VideoPlayer
                  key={video.id}
                  src={video.src}
                  poster={video.poster}
                  title={video.title}
                  thumbnailStoryboardUrl={video.thumbnailStoryboardUrl}
                  overlays={overlays}
                  sponsor={video.sponsor}
                  endCard={video.endCard}
                  ctaTimestamps={video.ctaTimestamps}
                  startTime={video.startTime}
                  aspectRatio={video.aspectRatio ?? "9:16"}
                  autoplay
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}
